import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from "@/components/ui/input-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	FIELD_CATEGORIES,
	FIELD_TYPE_CONFIGS,
} from "@/lib/fields/field-config";

import { PALETTE_CONFIG } from "../constants";

import type { FieldPaletteProps } from "../types";
import { FieldItem } from "./field-item";
import { PaletteHeader } from "./palette-header";

export function FullPalette({
	onAddField,
}: Pick<FieldPaletteProps, "onAddField" | "formSchema" | "onSchemaUpdate">) {
	const [searchTerm, setSearchTerm] = useState("");
	const t = useTranslations("fields");

	const filteredFields = useMemo(() => {
		if (!searchTerm.trim()) {
			return FIELD_TYPE_CONFIGS;
		}

		const term = searchTerm.toLowerCase();
		return FIELD_TYPE_CONFIGS.filter(
			(field) =>
				field.label.toLowerCase().includes(term) ||
				field.description.toLowerCase().includes(term) ||
				field.type.toLowerCase().includes(term)
		);
	}, [searchTerm]);

	const groupedFields = useMemo(() => {
		const groups: Record<string, typeof FIELD_TYPE_CONFIGS> = {};

		filteredFields.forEach((field) => {
			if (!groups[field.category]) {
				groups[field.category] = [];
			}
			groups[field.category].push(field);
		});

		return groups;
	}, [filteredFields]);

	return (
		<div
			aria-label={t("paletteAria")}
			className="flex h-full flex-col gap-6 border-border p-2 lg:border-r lg:p-4"
			role="complementary"
			style={{
				overscrollBehavior: "contain",
			}}
		>
			<div className="flex flex-col gap-4">
				<PaletteHeader
					description={t("headerDescription")}
					title={t("headerTitle")}
				/>
			</div>
			<InputGroup>
				<InputGroupAddon align="inline-start">
					<Search aria-hidden="true" className="size-4 text-muted-foreground" />
				</InputGroupAddon>
				<InputGroupInput
					aria-label={t("searchAria")}
					autoComplete="off"
					name="field-search"
					onChange={(e) => setSearchTerm(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Escape") {
							setSearchTerm("");
							e.currentTarget.blur();
						}
					}}
					placeholder={t("searchPlaceholder")}
					type="search"
					value={searchTerm}
				/>
			</InputGroup>

			<ScrollArea
				className="h-0 min-h-0 flex-1"
				style={{
					overscrollBehavior: "contain",
				}}
			>
				<div className="flex flex-col gap-4 rounded-2xl">
					{Object.entries(groupedFields).length > 0 ? (
						Object.entries(groupedFields).map(([key, fields]) => {
							const colCount = 2;
							const columns = Array.from({ length: colCount }, (_, colIdx) =>
								fields.filter((_, idx) => idx % colCount === colIdx)
							);

							return (
								<div className="flex flex-col gap-2" key={key}>
									<div
										aria-level={3}
										className="px-1 text-muted-foreground text-xs uppercase tracking-wide"
										role="heading"
									>
										{t(`categories.${key}`)}
									</div>
									<div
										aria-label={t("categoryFieldsAria", { category: t(`categories.${key}`) })}
										className="grid grid-cols-1 gap-2 rounded-2xl"
										role="list"
									>
										{columns.map((col, colIdx) => (
											<div className="flex flex-col gap-2" key={colIdx}>
												{col.map((f) => (
													<FieldItem
														fieldType={{
															type: f.type,
															label: f.label,
															description: f.description,
															icon: f.icon,
														}}
														key={f.type}
														onAddField={onAddField}
													/>
												))}
											</div>
										))}
									</div>
								</div>
							);
						})
					) : (
						<div
							aria-live="polite"
							className="flex flex-col items-center justify-center gap-2 py-8 text-center"
							role="status"
						>
							<Search
								aria-hidden="true"
								className="size-8 text-muted-foreground"
							/>
							<p className="text-muted-foreground text-sm">
								{t("noFieldsFound", { searchTerm })}
							</p>
							<p className="text-muted-foreground text-xs">
								{t("noFieldsHint")}
							</p>
						</div>
					)}
				</div>
			</ScrollArea>
		</div>
	);
}
